/**
 * Custom hook for dashboard data fetching, caching, and filter management.
 *
 * @returns Dashboard state including data, loading, filters, and action handlers.
 */
import { useState, useEffect, useRef } from 'react';

import axios from 'axios';
import api, { qualityAPI, suppliersAPI, coreAPI, performanceSettingsAPI, purchasingAPI, fetchAllPages } from '../lib/api';
import type { Company, Site, CompanyGroup, Warehouse } from '../lib/api';
import { getApiErrorMessage } from '../lib/api/types/common';
import type {
  DashboardData,
  DashboardFilterState,
  SavedDashboardFilter,
  ToastState,
  CommodityGroup,
  CommodityType,
} from '../components/dashboard/types';
import { getLast12MonthsRange } from '../components/dashboard/DashboardFilters';
import { effectiveDateTo } from '../components/dashboard/dashboardFilterUtils';
import { Cache } from '../lib/cache';
import { usePreferences } from '../contexts/PreferencesContext';
import { useView } from '../contexts/ViewContext';
import { useAuth } from '../contexts/AuthContext';
import { usePortalMode } from '../hooks/usePortalMode';

/** Debounce for auto-applying filter edits (typing a custom date = one fetch). */
export const FILTER_APPLY_DEBOUNCE_MS = 300;

export interface UseDashboardDataReturn {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  filters: DashboardFilterState;
  setFilters: React.Dispatch<React.SetStateAction<DashboardFilterState>>;
  suppliers: Company[];
  customers: Company[];
  sites: Site[];
  warehouses: Warehouse[];
  supplierGroups: CompanyGroup[];
  commodityGroups: CommodityGroup[];
  commodityTypes: CommodityType[];
  savedFilters: SavedDashboardFilter[];
  activeFilterPresetId: number | null;
  showSaveFilterDialog: boolean;
  setShowSaveFilterDialog: (show: boolean) => void;
  filterName: string;
  setFilterName: (name: string) => void;
  filterDescription: string;
  setFilterDescription: (desc: string) => void;
  isShared: boolean;
  setIsShared: (shared: boolean) => void;
  isDefault: boolean;
  setIsDefault: (isDefault: boolean) => void;
  toast: ToastState;
  setToast: React.Dispatch<React.SetStateAction<ToastState>>;
  exporting: boolean;
  dataPeriod: 'weekly' | 'monthly';
  setDataPeriod: (period: 'weekly' | 'monthly') => void;
  orderType: 'all' | 'scheduled' | 'spot';
  setOrderType: (type: 'all' | 'scheduled' | 'spot') => void;
  showToast: (message: string, type?: 'success' | 'error') => void;
  handleApplyFilters: () => void;
  handleResetFilters: () => void;
  handleSaveFilter: () => Promise<void>;
  loadFilterPreset: (preset: SavedDashboardFilter, skipFetch?: boolean) => void;
  fetchDashboardData: (forceRefresh?: boolean, filterOverrides?: DashboardFilterState, background?: boolean) => Promise<void>;
  // CR-4760: Pass the active tab so the CSV reflects what's on screen.
  // Compliance tab gets a focused compliance-only export instead of the
  // hardcoded purchasing metrics.
  handleExportReport: (activeTab?: string) => Promise<void>;
  loadDateAdherence: () => Promise<void>;
  loadCompanyKpi: () => Promise<void>;
  loadGroupKpi: () => Promise<void>;
  loadCostData: () => Promise<void>;
}

export function useDashboardData(
  _isComplianceMode: boolean,
  // Whether the visible tab actually consumes `data`. Tabs that fetch their own
  // payload (scheduled / spot-orders / po-lines) read none of it, yet every load
  // paid for the heaviest call on the dashboard regardless of who was looking.
  // Measured on a schedule-line tenant's dyno: `orders/dashboard_stats/` costs
  // ~6.4s cold (67 queries), and that tenant's landing tab is a self-fetching
  // one -- so the work was pure waste on the view opened every morning.
  //
  // Only that one call is gated. The cheaper companions still run, because
  // `perfSettings` carries `delivery_metric_mode`, which decides whether the
  // OTD tab is even visible; skipping it would flash a tab the tenant has
  // switched off. Switching to a consuming tab flips this true and refetches.
  needsHeavyData: boolean = true,
): UseDashboardDataReturn {
  const { preferences, isLoading: prefsLoading } = usePreferences();
  // Ref, not closure capture: fetchDashboardData is invoked from effects and
  // listeners created on earlier renders, which would otherwise freeze the
  // first render's value of the gate.
  const needsHeavyRef = useRef(needsHeavyData);
  needsHeavyRef.current = needsHeavyData;
  // The display currency must be read at FETCH time, not from whatever
  // closure an effect captured. The view-context / period effects call
  // fetchDashboardData from closures created before the effective
  // preferences arrived, which sent display_currency=GBP for an INR tenant
  // — money came back in £ under a ₹ label. A ref always holds the latest.
  const currencyRef = useRef(preferences.currency);
  currencyRef.current = preferences.currency;
  const lastFetchedCurrency = useRef<string>('');
  const { viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId, getViewQueryParams,
          selectedCompanyName, selectedSiteName, selectedGroupName, isLoading: viewContextLoading } = useView();
  const { isSupplier: portalIsSupplier, isHelix: portalIsHelix } = usePortalMode();
  // The performance-settings endpoint is IsHelixOrCustomerAdmin; a Customer
  // User's 403 was logged on every dashboard load (Customer review 11 Sep).
  const { user: authUser } = useAuth();
  const canReadPerfSettings = portalIsHelix || Boolean(authUser?.is_company_admin || authUser?.can_manage_users);

  const [dataPeriod, setDataPeriod] = useState<'weekly' | 'monthly'>('monthly');
  const [orderType, setOrderType] = useState<'all' | 'scheduled' | 'spot'>('all');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultDateRange = getLast12MonthsRange();
  const [filters, setFilters] = useState<DashboardFilterState>({
    dateFrom: defaultDateRange.dateFrom,
    dateTo: defaultDateRange.dateTo,
    supplier: '',
    customer: '',
    site: '',
    currency: preferences.currency,
    supplierGroup: '',
    strategicOnly: false,
    supplierType: '',
    commodityGroup: '',
    commodityType: '',
    siteCountry: '',
    supplierCountry: '',
    supplierCode: '',
    orderType: '',
    warehouse: '',
    dataMode: 'historical',
    callOffWarehouseBasis: false,
    division: '',
    // CR-4800
    approvalStage: '',
    riskCategory: '',
  });

  const [suppliers, setSuppliers] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<CompanyGroup[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);

  const [savedFilters, setSavedFilters] = useState<SavedDashboardFilter[]>([]);
  const [activeFilterPresetId, setActiveFilterPresetId] = useState<number | null>(null);
  const [showSaveFilterDialog, setShowSaveFilterDialog] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const [toast, setToast] = useState<ToastState>({ message: '', type: 'success', visible: false });
  const [exporting, setExporting] = useState(false);

  const isInitialized = useRef(false);
  // JSON of the filter state the most recent fetch used (see auto-apply effect).
  const lastAppliedFiltersKey = useRef('');
  const fetchInProgress = useRef(false);
  const hasInitiallyLoaded = useRef(false);
  const fetchIdRef = useRef(0);
  const viewContextInitialized = useRef(false);
  const prevPortalModeRef = useRef(portalIsSupplier);
  const prevDataPeriod = useRef(dataPeriod);
  const prevOrderType = useRef(orderType);
  const prevViewLevel = useRef(viewLevel);
  const prevSelectedCompanyId = useRef(selectedCompanyId);
  const prevSelectedSiteId = useRef(selectedSiteId);
  const prevSelectedGroupId = useRef(selectedGroupId);
  const includeDateAdherenceRef = useRef(false);
  // Per-tab lazy-load flags. Default tab only needs summary KPIs + charts +
  // supplier rankings; these heavier sections are fetched on demand when
  // the user opens the matching tab. Once loaded, the flag stays sticky so
  // subsequent refreshes keep the data fresh.
  const includeCompanyKpiRef = useRef(false);
  const includeGroupKpiRef = useRef(false);
  const includeCostDataRef = useRef(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 3000);
  };

  // Single initialization effect — wait for view context AND preferences to load
  // before first fetch, so the correct currency is used from the start
  useEffect(() => {
    if (isInitialized.current) return;
    if (viewContextLoading || prefsLoading) return;
    isInitialized.current = true;
    // This fetch already carries the resolved view scope; record it as the
    // baseline so the view-change effect below (which runs in the same
    // commit when ViewContext resolves) does not see null→id as a change
    // and fire a second, identical round of requests.
    prevViewLevel.current = viewLevel;
    prevSelectedCompanyId.current = selectedCompanyId;
    prevSelectedSiteId.current = selectedSiteId;
    prevSelectedGroupId.current = selectedGroupId;
    fetchDashboardData(true);
    fetchFilterOptions();
    fetchSavedFilters();
  }, [viewContextLoading, prefsLoading]);

  // CR-7099: refetch when the tab/window regains focus. The page has no
  // poll, so without this a dashboard left open while the user works
  // elsewhere (e.g. processing appeals in another tab) shows its
  // load-time data indefinitely. The server cache is version-keyed per
  // data change, so a focus refetch is a cheap cache hit unless the
  // underlying data actually moved. Throttled so rapid tab-flipping
  // doesn't spam requests. The listener goes through a ref because this
  // effect mounts once and fetchDashboardData's closure would otherwise
  // freeze the first render's filter state.
  const fetchRef = useRef<((force?: boolean, background?: boolean) => Promise<void>) | undefined>(undefined);
  const lastFocusRefetchRef = useRef(0);
  useEffect(() => {
    const onFocus = () => {
      if (!hasInitiallyLoaded.current) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusRefetchRef.current < 30_000) return;
      lastFocusRefetchRef.current = now;
      // Background: keep the rendered dashboard on screen while this runs.
      fetchRef.current?.(true, true);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // The gate opening is itself a fetch trigger: the user has just switched to a
  // tab that needs the payload we skipped. Only fires on the false -> true edge,
  // so tabs that never needed it cost nothing, and going back to a self-fetching
  // tab does not discard what we already hold.
  const prevNeedsHeavy = useRef(needsHeavyData);
  useEffect(() => {
    const opened = !prevNeedsHeavy.current && needsHeavyData;
    prevNeedsHeavy.current = needsHeavyData;
    if (!hasInitiallyLoaded.current) return;
    if (opened) fetchDashboardData(true);
  }, [needsHeavyData]);

  // Refetch on portal mode change
  useEffect(() => {
    if (!hasInitiallyLoaded.current) return;
    if (prevPortalModeRef.current === portalIsSupplier) return;
    prevPortalModeRef.current = portalIsSupplier;
    fetchDashboardData(true);
  }, [portalIsSupplier]);

  // Track view context initialization
  useEffect(() => {
    if (viewContextLoading) return;
    if (!viewContextInitialized.current) viewContextInitialized.current = true;
  }, [viewContextLoading]);

  // Refetch when view context (site/company/group selection) changes.
  // Always update the refs; only refetch once the init effect has issued the
  // first (already-scoped) fetch. The init effect waits for ViewContext to
  // resolve and records the resolved scope as the baseline, so a change
  // before init is absorbed by the first fetch and a change after it is
  // caught here — never both for the same scope.
  useEffect(() => {
    const viewLevelChanged = prevViewLevel.current !== viewLevel;
    const companyChanged = prevSelectedCompanyId.current !== selectedCompanyId;
    const siteChanged = prevSelectedSiteId.current !== selectedSiteId;
    const groupChanged = prevSelectedGroupId.current !== selectedGroupId;
    prevViewLevel.current = viewLevel;
    prevSelectedCompanyId.current = selectedCompanyId;
    prevSelectedSiteId.current = selectedSiteId;
    prevSelectedGroupId.current = selectedGroupId;
    if (!isInitialized.current) return;
    if (viewLevelChanged || companyChanged || siteChanged || groupChanged) {
      fetchDashboardData(true);
      // CR-4887: For supplier users, the customer hierarchy backing the
      // Company Site / Division filters changes when the user switches
      // their authorised supplier site (the backend keys customers off
      // profile.current_site_id). Refetch the filter options so the
      // dropdowns reflect the new supplier site's customer set.
      if (portalIsSupplier) fetchFilterOptions();
    }
  }, [viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId]);

  // Refetch on currency change. Compared against the currency the LAST
  // fetch actually sent (not against init flags): PreferencesContext starts
  // with isLoading=false and platform defaults, so the first fetch can go
  // out as GBP before the effective INR preference arrives — and the old
  // init-flag guard then swallowed the change, leaving £ numbers under a ₹
  // label for the rest of the session.
  useEffect(() => {
    if (!hasInitiallyLoaded.current) return;
    if (preferences.currency && lastFetchedCurrency.current !== preferences.currency) {
      fetchDashboardData(true);
    }
  }, [preferences.currency]);

  // Refetch on period/order type change
  useEffect(() => {
    const periodChanged = prevDataPeriod.current !== dataPeriod;
    const orderChanged = prevOrderType.current !== orderType;
    prevDataPeriod.current = dataPeriod;
    prevOrderType.current = orderType;
    if (hasInitiallyLoaded.current && (periodChanged || orderChanged)) fetchDashboardData(true);
  }, [dataPeriod, orderType]);

  // Auto-apply filter changes. The quick-range buttons ("This month (live)",
  // "This year", ...), the Historical/Live toggle and the custom From/To
  // dates all write `filters`, and the KPI "Period:" label reads straight
  // from that state — so a period change used to relabel the tiles while
  // the numbers stayed on the previous range until Refresh was pressed.
  // Any change that the last fetch has not already applied now refetches
  // the way Refresh does (W3-T5: the Live/Historical mode rides on the
  // filter state, so every fetch carries it). Debounced so typing a
  // custom date coalesces into one request, and keyed on the filters the
  // last fetch actually used so preset loads / resets (which fetch
  // explicitly with overrides) do not double-fire.
  useEffect(() => {
    if (!isInitialized.current) return;
    if (JSON.stringify(filters) === lastAppliedFiltersKey.current) return;
    const timer = setTimeout(() => {
      fetchDashboardData(true, filters);
    }, FILTER_APPLY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    const isHelixUser = portalIsHelix;
    try {
      if (isHelixUser) {
        // CR-5322: route the Supplier-Site dropdown through for-filter so
        // partially-onboarded suppliers (draft / pending_initial / invited /
        // due_diligence / not_approved) don't leak into the picker before
        // they're approved. Customers and groups still come from the
        // generic list — those are CUSTOMER companies / company-groups,
        // unaffected by the onboarding state machine.
        const [suppliersRes, customerCompanies, groupsRes, warehousesRes] = await Promise.all([
          coreAPI.getSupplierSitesForFilter().catch(() => ({ data: { results: [] } })),
          // Walk pages — the backend clamps page_size, so one request silently truncated.
          fetchAllPages<Company>((page) => coreAPI.getCompanies({ company_type: 'customer', page_size: 100, page })),
          coreAPI.getCompanyGroups({ company_type: 'supplier' }),
          coreAPI.getWarehouses().catch(() => ({ data: { results: [] } })),
        ]);
        setSuppliers(suppliersRes.data.results || []);
        setCustomers(customerCompanies);
        setSites(customerCompanies as unknown as Site[]);
        setSupplierGroups(groupsRes.data.results || groupsRes.data || []);
        setWarehouses(warehousesRes.data.results || warehousesRes.data || []);
      } else {
        // CR-4887: supplier-portal users want CUSTOMER hierarchy in the
        // Company Site / Division dropdowns, not their own supplier group.
        // The opt-in ?context=customer_filter parameter flips the accessible
        // endpoints to return customers-served. Customer-portal users get
        // their own group siblings as before.
        const filterContext = portalIsSupplier ? { context: 'customer_filter' as const } : undefined;
        // CR-4887 round 2 follow-up: the Supplier Group dropdown is labelled
        // "Supplier Group" and should show the logged-in supplier's own
        // group (Skybridge Components), not the customer-side groups the
        // supplier serves. Drop customer_filter from the supplier-groups
        // call so the backend returns the supplier's effective group via
        // the round-2 _effective_supplier_company_id helper.
        const [companiesRes, sitesRes, groupsRes, supplierSitesRes, warehousesRes] = await Promise.all([
          coreAPI.getAccessibleCompanies(filterContext).catch(() => ({ data: { results: [] } })),
          coreAPI.getAccessibleSites(filterContext).catch(() => ({ data: [] })),
          coreAPI.getSupplierGroupsForFilter(undefined).catch(() => ({ data: { results: [] } })),
          coreAPI.getSupplierSitesForFilter().catch(() => ({ data: { results: [] } })),
          coreAPI.getAccessibleWarehouses().catch(() => ({ data: [] })),
        ]);
        const accessibleCompanies = companiesRes.data.results || companiesRes.data || [];
        if (portalIsSupplier) {
          setCustomers(accessibleCompanies.filter((c: Company) => c.company_type === 'customer'));
        } else {
          setCustomers([]);
        }
        // Supplier-Site dropdown source. Portal-aware on the backend:
        // customer portal -> traded suppliers; supplier portal -> own
        // group siblings; helix -> all suppliers.
        setSuppliers(supplierSitesRes.data.results || []);
        const siteData = sitesRes.data as unknown;
        const accessibleSites = Array.isArray(siteData) ? siteData : ((siteData as { results?: unknown[] })?.results || []);
        setSites(accessibleSites);
        setSupplierGroups(groupsRes.data.results || []);
        const whData = warehousesRes.data as unknown;
        const accessibleWarehouses = Array.isArray(whData) ? whData : ((whData as { results?: unknown[] })?.results || []);
        setWarehouses(accessibleWarehouses as Warehouse[]);
      }
      // CR-6355: Populate the Commodity Group / Commodity Type dashboard
      // filters from the Admin Setup → Manage Commodity Groups and Types
      // master data. Reads are company-scoped on the backend
      // (get_active_company) and open to every authenticated user, so the
      // same call works in all portal modes. Mirrors the Admin Setup source
      // (CommodityGroupsSection) so the filter options stay consistent with
      // the configured master data.
      const [commodityGroupsRes, commodityTypeRows] = await Promise.all([
        purchasingAPI.getCommodityGroups({ page_size: 100 }).catch(() => ({ data: [] })),
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        fetchAllPages((page) => purchasingAPI.getCommodityTypes({ page_size: 100, page })).catch(() => []),
      ]);
      const cgData = commodityGroupsRes.data as unknown;
      setCommodityGroups(Array.isArray(cgData) ? cgData : ((cgData as { results?: CommodityGroup[] })?.results || []));
      setCommodityTypes(commodityTypeRows as unknown as CommodityType[]);
    } catch {
      setSuppliers([]);
      setCustomers([]);
      setSites([]);
      setWarehouses([]);
      setSupplierGroups([]);
      setCommodityGroups([]);
      setCommodityTypes([]);
    }
  };

  const fetchSavedFilters = async (isInitialLoad: boolean = true) => {
    try {
      const response = await api.get('/core/dashboard-filter-presets/');
      setSavedFilters(response.data.results || response.data || []);
      const defaultFilter = (response.data.results || response.data || []).find((f: SavedDashboardFilter) => f.is_default);
      if (defaultFilter) loadFilterPreset(defaultFilter, isInitialLoad);
    } catch (err) {
      console.error('Failed to fetch saved filters:', err);
    }
  };

  const handleSaveFilter = async () => {
    if (!filterName.trim()) {
      showToast('Please enter a filter name', 'error');
      return;
    }
    try {
      const payload = {
        name: filterName, description: filterDescription,
        date_from: filters.dateFrom || null, date_to: filters.dateTo || null,
        supplier: filters.supplier || null, customer: filters.customer || null,
        site: filters.site || null, currency: filters.currency,
        is_shared: isShared, is_default: isDefault,
      };
      await api.post('/core/dashboard-filter-presets/', payload);
      setShowSaveFilterDialog(false);
      setFilterName('');
      setFilterDescription('');
      setIsShared(false);
      setIsDefault(false);
      fetchSavedFilters(false);
      showToast('Filter saved successfully!', 'success');
    } catch (err: unknown) {
      console.error('Failed to save filter:', err);
      showToast(getApiErrorMessage(err, 'Failed to save filter'), 'error');
    }
  };

  const loadFilterPreset = (preset: SavedDashboardFilter, skipFetch: boolean = false) => {
    const newFilters: DashboardFilterState = {
      dateFrom: preset.date_from || '', dateTo: preset.date_to || '',
      supplier: preset.supplier?.toString() || '', customer: preset.customer?.toString() || '',
      site: preset.site?.toString() || '', currency: preset.currency || preferences.currency,
      supplierGroup: preset.supplier_group?.toString() || '',
      strategicOnly: preset.strategic_only || false,
      supplierType: preset.supplier_type || '',
      commodityGroup: preset.commodity_group?.toString() || '',
      commodityType: preset.commodity_type?.toString() || '',
      siteCountry: '', supplierCountry: '', supplierCode: '', orderType: '', warehouse: '', dataMode: 'historical', division: '',
      approvalStage: '', riskCategory: '',
      // Not persisted on saved presets — the basis is a way of reading the
      // current view, not part of the saved selection, so it resets to the
      // GRN default whenever a preset is loaded.
      callOffWarehouseBasis: false,
    };
    setFilters(newFilters);
    setActiveFilterPresetId(preset.id);
    if (!skipFetch) fetchDashboardData(true, newFilters);
  };

  // `background` = revalidate without disturbing what the user is looking at.
  // A focus refetch must not blank the page: it sets neither the blocking
  // loading state nor the error state, and simply swaps the data in when it
  // arrives. Without it, returning to the tab replaced a fully-rendered
  // dashboard with the full-screen "Loading dashboard data from database..."
  // spinner every 30 seconds, even when nothing had changed.
  const fetchDashboardData = async (forceRefresh: boolean = false, filterOverrides?: DashboardFilterState, background: boolean = false) => {
    // Track fetch ID to discard stale responses from superseded fetches
    const currentFetchId = ++fetchIdRef.current;
    const effectiveFilters = filterOverrides || filters;
    // Remember what this fetch applied so the auto-apply effect only fires
    // for filter state the dashboard is not already showing.
    lastAppliedFiltersKey.current = JSON.stringify(effectiveFilters);
    // CR-7099: capture the filters THIS fetch actually uses, so a focus
    // refetch reproduces the displayed data. A plain closure would be wrong
    // for override-driven fetches (saved-preset load, filter reset): the
    // render's `filters` state there is still the pre-override value, and a
    // later focus refetch would silently replace the preset's numbers.
    fetchRef.current = (force: boolean = true, background: boolean = false) => fetchDashboardData(force, effectiveFilters, background);

    const cacheFilters = {
      ...effectiveFilters,
      display_currency: currencyRef.current || 'GBP',
      viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId,
      dataPeriod, orderType,
      includeDateAdherence: includeDateAdherenceRef.current,
      includeCompanyKpi: includeCompanyKpiRef.current,
      includeGroupKpi: includeGroupKpiRef.current,
      includeCostData: includeCostDataRef.current,
    };
    const filterKey = JSON.stringify(cacheFilters);
    // Bump cache version so every client's stale sessionStorage entry is
    // orphaned on next dashboard load. The OTIF chart was using include_in_otif
    // filter but drilldown wasn't, causing tooltip/drilldown count mismatch.
    // Chart now uses same queryset as drilldown.
    const CACHE_VERSION = 'v9';
    const CACHE_KEY = `dashboard_data_${CACHE_VERSION}_${filterKey}`;

    if (!forceRefresh) {
      const cachedData = Cache.get(CACHE_KEY) as DashboardData | null;
      if (cachedData) {
        setData(cachedData);
        hasInitiallyLoaded.current = true;
        setLoading(false);
        return;
      }
    }

    try {
      fetchInProgress.current = true;
      if (!background) {
        setLoading(true);
        setError(null);
      }

      const filterParams: Record<string, string | number | boolean> = {};
      if (effectiveFilters.dateFrom) filterParams['date_from'] = effectiveFilters.dateFrom;

      // CR-4339: Historical mode caps date_to to end of previous month
      // (excludes current incomplete month); Live mode uses actual date_to.
      // CR-6003 round 3: formatted in LOCAL time. W3-T5: the same helper
      // feeds every drill-down, so ring and grid share one window.
      const dateTo = effectiveDateTo(effectiveFilters.dateTo, effectiveFilters.dataMode);
      if (dateTo) filterParams['date_to'] = dateTo;

      if (effectiveFilters.supplier) filterParams['supplier'] = effectiveFilters.supplier;
      if (effectiveFilters.customer) filterParams['customer'] = effectiveFilters.customer;
      if (effectiveFilters.site) filterParams['site'] = effectiveFilters.site;
      // CR-7229: Warehouse filter. The panel rendered the dropdown and counted
      // it toward the "N filters active" badge, but never sent it — so every
      // tab fed by this fetch ignored the selection. Cascades off site (the
      // panel clears warehouse whenever site changes).
      if (effectiveFilters.warehouse) filterParams['warehouse'] = effectiveFilters.warehouse;
      if (effectiveFilters.supplierType) filterParams['supplier_type'] = effectiveFilters.supplierType;
      // CR-4557: Pass country filters to backend
      if (effectiveFilters.siteCountry) filterParams['site_country'] = effectiveFilters.siteCountry;
      if (effectiveFilters.supplierCountry) filterParams['supplier_country'] = effectiveFilters.supplierCountry;
      // CR-4349: Division filter — backend supports `group` query param
      if (effectiveFilters.division) filterParams['group'] = effectiveFilters.division;
      // CR-4886: Supplier Group filter — wire to backend so combo
      // (supplier_group + supplier site) actually narrows the dataset
      // instead of silently dropping the group selection.
      if (effectiveFilters.supplierGroup) filterParams['supplier_group'] = effectiveFilters.supplierGroup;
      // CR-6355: Commodity filters — dashboard_stats reads these via the
      // PO-line lookup (lines__commodity_group / lines__commodity_type),
      // narrowing the dataset to POs that have a matching line. Without
      // these params the dropdowns would populate but selecting one would
      // have no effect on the metrics/charts/drill-downs.
      if (effectiveFilters.commodityGroup) filterParams['lines__commodity_group'] = effectiveFilters.commodityGroup;
      if (effectiveFilters.commodityType) filterParams['lines__commodity_type'] = effectiveFilters.commodityType;
      if (effectiveFilters.strategicOnly) filterParams['strategic_only'] = true;
      // CR-7028: Supplier code filter — queries the supplier company's
      // supplier_code field (not the PO line's, which is often empty).
      if (effectiveFilters.supplierCode) filterParams['supplier_code'] = effectiveFilters.supplierCode;
      // Basis toggle, not a filter: measures call-off suppliers from their
      // warehouse-booking date while everyone else stays on GRN.
      if (effectiveFilters.callOffWarehouseBasis) filterParams['call_off_warehouse_basis'] = true;
      filterParams['display_currency'] = currencyRef.current || 'GBP';
      lastFetchedCurrency.current = currencyRef.current || 'GBP';
      if (dataPeriod) filterParams['period'] = dataPeriod;
      if (orderType && orderType !== 'all') filterParams['order_type'] = orderType;
      // W3-T5 (Aaron, 4 Sep 2026): `data_mode` decides the ARREARS TERM
      // (Historical = frozen month-end snapshots, Live = today's stock), so
      // it is a property of the filter state and rides on EVERY request —
      // tab opens, preset loads, currency changes, focus revalidation and
      // the Refresh button alike. It used to double as a per-call cache
      // bypass, which made Refresh silently flip a Historical dashboard to
      // Live figures for one request. The server cache is version-bumped
      // on every PO/line/GRN/NCR write (CR-7099), so no bypass is needed.
      if (effectiveFilters.dataMode === 'live') {
        filterParams['data_mode'] = 'live';
      }

      // Perf: opt into the lazy gating pattern. Default tab gets only summary
      // KPIs + charts; per-tab sections are fetched on demand below.
      filterParams['lazy'] = '1';
      if (includeDateAdherenceRef.current) {
        filterParams['include_date_adherence'] = '1';
      }
      if (includeCompanyKpiRef.current) {
        filterParams['include_company_kpi'] = '1';
      }
      if (includeGroupKpiRef.current) {
        filterParams['include_group_kpi'] = '1';
      }
      if (includeCostDataRef.current) {
        filterParams['include_cost_data'] = '1';
      }

      // CR-6219: always carry the active view context (group/division/site) so
      // the dashboard tiles scope to the selected node, not just on manual
      // refresh. The backend (get_accessible_* / dashboard_stats) now honours
      // these params consistently.
      const viewParams = getViewQueryParams();
      if (viewParams['group']) filterParams['group'] = viewParams['group'];
      if (viewParams['company']) {
        // Supplier users: filter by supplier ID, not customer ID
        filterParams[portalIsSupplier ? 'supplier' : 'customer'] = viewParams['company'];
      }
      if (viewParams['site']) filterParams['site'] = viewParams['site'];

      const viewContextParams: Record<string, string | number> = {};
      if (viewParams['group']) viewContextParams['group'] = viewParams['group'];
      if (viewParams['company']) viewContextParams['company'] = viewParams['company'];
      if (viewParams['site']) viewContextParams['site'] = viewParams['site'];

      const supplierStatsParams: Record<string, string | number | boolean> = { ...viewContextParams };
      if (effectiveFilters.strategicOnly) supplierStatsParams['strategic_only'] = true;
      // R-261: the Performance Summary's "Active Suppliers" is the one field
      // of this payload that reaches a rendered tile, and it sat beside four
      // tiles that DO narrow — so with a supplier site selected it read 4
      // against their 1. These params are built from viewContextParams
      // (group/company/site) alone, which is the whole of the defect: the
      // filter bar never reached the endpoint.
      if (effectiveFilters.supplier) supplierStatsParams['supplier'] = effectiveFilters.supplier;
      if (effectiveFilters.supplierGroup) supplierStatsParams['supplier_group'] = effectiveFilters.supplierGroup;
      // Parity: the supplier list OTIF column must read on the same basis as
      // the KPI tile, so a supplier never shows two different scores.
      if (effectiveFilters.callOffWarehouseBasis) supplierStatsParams['call_off_warehouse_basis'] = true;

      const [coreStats, purchasingStats, qualityStats, supplierStats, perfSettings] = await Promise.all([
        coreAPI.getDashboardStats(viewContextParams),
        // The gated one. An empty payload is safe: every field below reads
        // through `|| 0` / `?? null`, so the shape stays valid and the tabs
        // that do consume it are never rendered while it is skipped.
        needsHeavyRef.current
          ? api.get('/purchasing/orders/dashboard_stats/', { params: filterParams })
          : Promise.resolve({ data: {} as Record<string, never> }),
        // Customer users whose site permissions carry can_quality=False are
        // 403'd by HasQualityModuleAccess. Inside Promise.all that rejection
        // took the WHOLE dashboard down — "Failed to load dashboard data /
        // You do not have access to the Quality module." — locking a
        // purchasing-only user out of purchasing, delivery and supplier data
        // they are entitled to. Degrade to empty NCR stats instead, the same
        // way the settings call below tolerates its 403.
        qualityAPI.getNCRStats(viewContextParams).catch(() => null),
        suppliersAPI.getDashboardStats(supplierStatsParams),
        // Customer-side settings (delivery_metric_mode). The dashboard_stats
        // payload already carries the mode, so this admin-only endpoint is only
        // the fallback when that payload is skipped — and only for users it
        // serves: it 403s for suppliers and for non-admin customer users
        // (Customer review 11 Sep: the Customer User's console logged the 403
        // on every load). Otherwise the 'both' fallback applies.
        (portalIsSupplier || needsHeavyRef.current || !canReadPerfSettings)
          ? Promise.resolve(null)
          : performanceSettingsAPI.getSettings().catch(() => null),
      ]);

      const mappedData: DashboardData = {
        companies: { total: coreStats.data.total_companies || 0, customers: coreStats.data.total_customers || 0, suppliers: coreStats.data.total_suppliers || 0 },
        sites: { total: coreStats.data.total_sites || 0 },
        users: { total: coreStats.data.total_users || 0, pendingApprovals: coreStats.data.pending_approvals || 0 },
        purchasing: { totalPOs: purchasingStats.data.total_pos || 0, openPOs: purchasingStats.data.open_pos || 0, totalLines: purchasingStats.data.total_lines || 0, byStatus: purchasingStats.data.by_status || {}, totalValue: parseFloat(purchasingStats.data.total_value?.toString() || '0') },
        // CR-4641 round 2: preserve null for ppm/otif/otd so chart visibility
        // can be driven by data presence (backend returns null when the
        // SupplierProfile flag excludes all matching suppliers). Old `|| 0`
        // collapsed null → 0, hiding the signal.
        quality: { ppm: purchasingStats.data.ppm ?? null, ppmTarget: purchasingStats.data.targets?.ppm ?? 1500, ncrs: purchasingStats.data.ncrs || 0, ncrsByStatus: qualityStats?.data.by_status || {}, ncrsBySeverity: qualityStats?.data.by_severity || {}, otif: purchasingStats.data.otif ?? null, otifTarget: purchasingStats.data.targets?.otif ?? 95 }, // Q46: fallback = the settings field's own default
        delivery: { otd: purchasingStats.data.otd ?? null, otdTarget: purchasingStats.data.targets?.otd ?? 90, appeals: purchasingStats.data.appeals || 0, appealsBreakdown: purchasingStats.data.appeals_breakdown || undefined, openOrderValue: parseFloat(purchasingStats.data.open_order_value?.toString() || '0') },
        suppliers: { total: supplierStats.data.total_suppliers || 0, active: supplierStats.data.active_suppliers || 0, preferred: supplierStats.data.preferred_suppliers || 0, avgQuality: supplierStats.data.avg_quality_rating || 0, avgDelivery: supplierStats.data.avg_delivery_rating || 0 },
        cost: { totalSpend: parseFloat(purchasingStats.data.total_spend?.toString() || '0'), historicalArrears: parseFloat(purchasingStats.data.arrears_value?.toString() || '0') },
        charts: {
          ppmTrend: purchasingStats.data.charts?.ppmTrend || [], otifData: purchasingStats.data.charts?.otifData || [],
          spendByMonth: purchasingStats.data.charts?.spendByMonth || [], otdData: purchasingStats.data.charts?.otdData || [],
          liveNCRsOverdue: purchasingStats.data.charts?.liveNCRsOverdue || [], historicalArrears: purchasingStats.data.charts?.historicalArrears || [], openArrearsByDueMonth: purchasingStats.data.charts?.openArrearsByDueMonth || [], arrearsBacklog: purchasingStats.data.charts?.arrearsBacklog || [],
          appealsTrend: purchasingStats.data.charts?.appealsTrend || [], ppmNCRs: purchasingStats.data.charts?.ppmNCRs || [],
          ppmGRNs: purchasingStats.data.charts?.ppmGRNs || [], otifLines: purchasingStats.data.charts?.otifLines || [],
          otifPOL: purchasingStats.data.charts?.otifPOL || [], otifArrears: purchasingStats.data.charts?.otifArrears || [],
          otifFailed: purchasingStats.data.charts?.otifFailed || [], spendLines: purchasingStats.data.charts?.spendLines || [],
          otdOntime: purchasingStats.data.charts?.otdOntime || [], otdEarly: purchasingStats.data.charts?.otdEarly || [],
          otdLate: purchasingStats.data.charts?.otdLate || [], otdAll: purchasingStats.data.charts?.otdAll || [],
          ncrDetails: purchasingStats.data.charts?.ncrDetails || [], arrearsDetails: purchasingStats.data.charts?.arrearsDetails || [],
          milestones: purchasingStats.data.charts?.milestones || [], dateAdherenceData: purchasingStats.data.charts?.dateAdherenceData || [],
        },
        siteKPI: { otifBySite: purchasingStats.data.company_kpi?.otif_by_company || [], deliveryBySite: purchasingStats.data.company_kpi?.delivery_by_company || [], ppmBySite: purchasingStats.data.company_kpi?.ppm_by_company || [], ncrBreakdown: purchasingStats.data.company_kpi?.ncr_breakdown || [] },
        groupKpi: purchasingStats.data.group_kpi || undefined,
        costData: purchasingStats.data.costData || undefined,
        targets: purchasingStats.data.targets || undefined,
        delivery_metric_mode: purchasingStats.data.delivery_metric_mode || perfSettings?.data?.delivery_metric_mode || 'both',
      };

      // Discard stale responses if a newer fetch has started
      if (currentFetchId !== fetchIdRef.current) return;

      setData(mappedData);
      hasInitiallyLoaded.current = true;
      Cache.set(CACHE_KEY, mappedData, 5 * 60 * 1000);
      // The effective currency can change while this request is in flight
      // (preferences arrive after the first fetch). Converge on the latest
      // one rather than leaving money in the currency we happened to ask for.
      if ((currencyRef.current || 'GBP') !== lastFetchedCurrency.current) {
        fetchDashboardData(true);
        return;
      }
    } catch (err: unknown) {
      if (currentFetchId !== fetchIdRef.current) return;
      // A timeout (ECONNABORTED) is a real failure, not a benign cancellation:
      // surface it so the user gets an error with a retry instead of being
      // left on a spinner that can never resolve.
      const isTimeout = err && typeof err === 'object' && 'code' in err && err.code === 'ECONNABORTED';
      if (!isTimeout) {
        if (axios.isCancel(err) || (err instanceof Error && err.name === 'CanceledError')) return;
        if (err && typeof err === 'object' && 'code' in err && err.code === 'ERR_CANCELED') return;
      }
      console.error('Failed to fetch dashboard data:', err);
      // A background revalidation that fails leaves the last good data on
      // screen. Surfacing an error for a refresh the user never asked for
      // would throw away a working dashboard over a transient blip.
      if (!background) {
        setError(getApiErrorMessage(err, isTimeout ? 'Dashboard data timed out. Please retry.' : 'Failed to load dashboard data'));
      }
    } finally {
      // Only update loading state if this is still the latest fetch.
      // Otherwise a superseded fetch would clear loading while the new one is still running.
      if (currentFetchId === fetchIdRef.current) {
        fetchInProgress.current = false;
        setLoading(false);
      }
    }
  };

  // Explicit Apply mirrors the auto-apply path.
  const handleApplyFilters = () => fetchDashboardData(true);

  const handleResetFilters = () => {
    setActiveFilterPresetId(null);
    const resetDateRange = getLast12MonthsRange();
    setFilters({
      dateFrom: resetDateRange.dateFrom, dateTo: resetDateRange.dateTo,
      supplier: '', customer: '', site: '', currency: preferences.currency,
      supplierGroup: '', strategicOnly: false, supplierType: '',
      commodityGroup: '', commodityType: '', siteCountry: '', supplierCountry: '',
      supplierCode: '', orderType: '', warehouse: '', dataMode: 'historical', division: '',
      approvalStage: '', riskCategory: '', callOffWarehouseBasis: false,
    });
    // The auto-apply effect picks the reset state up and refetches with it.
    // (The previous setTimeout fetch read `filters` from this render's
    // closure, i.e. the PRE-reset values.)
  };

  // Lazy-load the per-supplier/per-month adherence dataset. Called when
  // the user opens the Date Adherence tab — avoids paying for the 100KB+
  // payload on every default dashboard load.
  const loadDateAdherence = async () => {
    if (includeDateAdherenceRef.current) return;
    includeDateAdherenceRef.current = true;
    await fetchDashboardData(true);
  };

  const loadCompanyKpi = async () => {
    if (includeCompanyKpiRef.current) return;
    includeCompanyKpiRef.current = true;
    await fetchDashboardData(true);
  };

  const loadGroupKpi = async () => {
    if (includeGroupKpiRef.current) return;
    includeGroupKpiRef.current = true;
    await fetchDashboardData(true);
  };

  const loadCostData = async () => {
    if (includeCostDataRef.current) return;
    includeCostDataRef.current = true;
    await fetchDashboardData(true);
  };

  const handleExportReport = async (activeTab?: string) => {
    if (!data) {
      showToast('No data to export', 'error');
      return;
    }
    try {
      setExporting(true);
      const rows: string[] = [];

      const title = activeTab === 'compliance' ? 'Compliance Dashboard Report' : 'Dashboard Report';
      rows.push(title);
      rows.push(`Generated: ${new Date().toLocaleString()}`);
      rows.push(`Currency: ${preferences.currency}`);
      if (viewLevel) {
        rows.push(`View Level: ${viewLevel}`);
        if (selectedCompanyName) rows.push(`Company: ${selectedCompanyName}`);
        if (selectedSiteName) rows.push(`Company: ${selectedSiteName}`);
        if (selectedGroupName) rows.push(`Group: ${selectedGroupName}`);
      }
      rows.push('');

      // CR-4760: Compliance dashboard exports its own focused dataset.
      // The purchasing metrics below are irrelevant to that view.
      if (activeTab === 'compliance') {
        const { fetchComplianceExportData } = await import('../components/dashboard/complianceExportData');
        // CR-4760 rework: pass the same view context ComplianceTab uses when
        // it fetches /drilldown/suppliers_by_approval/buckets/, otherwise the
        // exported Approval Stage counts drift from what's on screen.
        const compliance = await fetchComplianceExportData({
          isComplianceMode: _isComplianceMode,
          viewLevel: viewLevel || undefined,
          viewQueryParams: getViewQueryParams(),
        });

        rows.push('Suppliers by Approval Stage', 'Stage,Count,Percentage');
        compliance.suppliersByApproval.forEach((b) => {
          rows.push(`${b.name},${b.value},${b.percentage}%`);
        });
        rows.push('');

        rows.push('Suppliers by Risk Category', 'Category,Count,Percentage');
        compliance.suppliersByRisk.forEach((b) => {
          rows.push(`${b.name},${b.value},${b.percentage}%`);
        });
        rows.push('');

        rows.push('Questionnaires by Status', 'Status,Count,Percentage');
        compliance.questionnairesByStatus.forEach((b) => {
          rows.push(`${b.name},${b.value},${b.percentage}%`);
        });
        rows.push('');

        if (!compliance.isComplianceMode) {
          rows.push('Contracts by Renewal', 'Type,Count,Percentage');
          compliance.contractsByAutoRenew.forEach((b) => {
            rows.push(`${b.name},${b.value},${b.percentage}%`);
          });
          rows.push('');

          rows.push('Summary');
          rows.push(`Live Contracts,${compliance.liveContracts}`);
          rows.push(`Suppliers Awaiting Review,${compliance.docsAwaitingRead}`);
          rows.push('');
        } else {
          rows.push('Summary');
          rows.push(`Suppliers Awaiting Review,${compliance.docsAwaitingRead}`);
          rows.push('');
        }

        const csvContent = rows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `compliance-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast('Report exported successfully!', 'success');
        return;
      }

      rows.push('Quality Metrics');
      rows.push(`PPM,${data.quality.ppm}`, `PPM Target,${data.quality.ppmTarget}`, `NCRs,${data.quality.ncrs}`, `OTIF,${data.quality.otif}%`, `OTIF Target,${data.quality.otifTarget}%`, '');

      rows.push('Delivery Metrics');
      rows.push(`OTD,${data.delivery.otd}%`, `OTD Target,${data.delivery.otdTarget}%`, `Appeals,${data.delivery.appeals}`, `Open Order Value,${data.delivery.openOrderValue}`, '');

      rows.push('Cost Metrics');
      rows.push(`Total Spend,${data.cost.totalSpend}`, `Historical Arrears,${data.cost.historicalArrears}`, '');

      rows.push('Purchasing');
      rows.push(`Total POs,${data.purchasing.totalPOs}`, `Total Lines,${data.purchasing.totalLines}`, `Total Value,${data.purchasing.totalValue}`, '');

      rows.push('Suppliers');
      rows.push(`Total,${data.suppliers.total}`, `Active,${data.suppliers.active}`, `Strategic,${data.suppliers.preferred}`, '');

      if (data.charts.ppmTrend && data.charts.ppmTrend.length > 0) {
        rows.push('PPM Trend', 'Month,PPM,Target');
        data.charts.ppmTrend.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['ppm'] || 0},${item['target'] || 0}`);
        });
        rows.push('');
      }
      if (data.charts.otifData && data.charts.otifData.length > 0) {
        rows.push('OTIF Trend', 'Month,OTIF %,Target,OTIF Lines,Arrears Lines,Failed Lines');
        data.charts.otifData.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['otif'] || 0},${item['target'] || 0},${item['otifCount'] || 0},${item['arrears'] || 0},${item['failed'] || 0}`);
        });
        rows.push('');
      }
      if (data.charts.otdData && data.charts.otdData.length > 0) {
        rows.push('OTD Trend', 'Month,OTD %,Target,On Time,Early,Late');
        data.charts.otdData.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['otd'] || 0},${item['target'] || 0},${item['onTime'] || 0},${item['early'] || 0},${item['late'] || 0}`);
        });
        rows.push('');
      }
      if (data.charts.spendByMonth && data.charts.spendByMonth.length > 0) {
        rows.push('Spend by Month', 'Month,Spend');
        data.charts.spendByMonth.forEach((item: Record<string, unknown>) => {
          rows.push(`${item['month'] || ''},${item['spend'] || 0}`);
        });
        rows.push('');
      }

      const csvContent = rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('Report exported successfully!', 'success');
    } catch (err) {
      console.error('Export failed:', err);
      showToast('Failed to export report', 'error');
    } finally {
      setExporting(false);
    }
  };

  return {
    data, loading, error, filters, setFilters,
    suppliers, customers, sites, warehouses, supplierGroups, commodityGroups, commodityTypes,
    savedFilters, activeFilterPresetId,
    showSaveFilterDialog, setShowSaveFilterDialog,
    filterName, setFilterName, filterDescription, setFilterDescription,
    isShared, setIsShared, isDefault, setIsDefault,
    toast, setToast, exporting,
    dataPeriod, setDataPeriod, orderType, setOrderType,
    showToast, handleApplyFilters, handleResetFilters,
    handleSaveFilter, loadFilterPreset, fetchDashboardData,
    handleExportReport,
    loadDateAdherence,
    loadCompanyKpi,
    loadGroupKpi,
    loadCostData,
  };
}
